import React, { useEffect, useState } from 'react';
import Skeleton, { SkeletonTheme } from 'react-loading-skeleton';
import 'react-loading-skeleton/dist/skeleton.css';
import { useTheme } from '../../hooks/useTheme';
import { Video, Youtube, Play, FileText, Download, ExternalLink } from 'lucide-react';
import VideoModal from '../../components/VideoModal';
import api from '../../api/api';

/**
 * Tutorials — the Learning Resources cards, managed from the admin panel
 * (Lookup Management -> Resources, placement = 'tutorials'). A resource can be
 * a YouTube video (opens in the VideoModal), an external link, or an uploaded
 * file (download). Contact Support lives on its own page.
 */

const fileHref = (id) => `${api.defaults.baseURL || ''}/resources/${id}/file`;

// Pull an 11-char video id out of any common YouTube form (youtu.be/ID,
// watch?v=ID, embed/ID, shorts/ID) or accept a bare id. Returns '' for anything
// we can't recognize — the caller must treat an empty id as "not a real video"
// (fall back to opening the raw link) instead of feeding a broken id to the
// thumbnail/embed, which is how a mis-entered URL turned into a dead click.
const extractVideoId = (url) => {
    if (!url) return '';
    const s = String(url).trim();
    const m = s.match(/(?:youtu\.be\/|[?&]v=|embed\/|shorts\/)([\w-]{11})/);
    if (m) return m[1];
    if (/^[\w-]{11}$/.test(s)) return s; // bare id
    return '';
};

// NOTE: these cards are declared at module scope on purpose. Defining them
// inside TutorialsSupport made each render a brand-new component type, so React
// remounted every card on every render — the thumbnail's onLoad then re-fired
// markLoaded → re-render → remount in an endless loop, and clicks never landed.
const VideoCard = ({ r, isDarkMode, sub, flatCard, loaded, markLoaded, onOpen }) => {
    const vid = extractVideoId(r.url);
    // A valid id plays in-app; a mis-entered URL still opens the raw link in a
    // new tab rather than doing nothing; with neither, the card is inert.
    const handleClick = () => {
        if (vid) onOpen(vid);
        else if (r.url) window.open(r.url, '_blank', 'noopener,noreferrer');
    };
    return (
        <button type="button" onClick={handleClick}
            className={`group flex flex-col text-left rounded-xl border overflow-hidden transition-all hover:shadow-lg ${flatCard}`}>
            <div className="relative aspect-video overflow-hidden bg-slate-900 shrink-0">
                {vid && !loaded[r.id] && <Skeleton borderRadius={0} className="!absolute !inset-0 !block !h-full !w-full" />}
                {vid && (
                    <img src={`https://img.youtube.com/vi/${vid}/hqdefault.jpg`} alt={r.title} loading="lazy"
                        onLoad={() => markLoaded(r.id)} onError={(e) => { e.currentTarget.style.display = 'none'; markLoaded(r.id); }}
                        className={`absolute inset-0 w-full h-full object-cover transition-opacity duration-300 ${loaded[r.id] ? 'opacity-100' : 'opacity-0'}`} />
                )}
                <span className="absolute inset-0 flex items-center justify-center">
                    <span className="w-12 h-12 rounded-full bg-white/90 flex items-center justify-center text-indigo-600 shadow-lg transition-transform group-hover:scale-110">
                        <Play size={20} className="ml-0.5" fill="currentColor" />
                    </span>
                </span>
            </div>
            <div className="p-3">
                <p className={`text-[11px] font-bold uppercase tracking-wide ${sub}`}>Watch</p>
                {/* Reserve two lines so 1- and 2-line titles leave every card the
                    same height; line-clamp keeps a very long title from blowing it up. */}
                <p className={`text-sm font-bold mt-0.5 line-clamp-2 min-h-[2.5rem] ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>{r.title}</p>
            </div>
        </button>
    );
};

const LinkCard = ({ r, isDarkMode, flatCard }) => {
    const isFile = r.source === 'file';
    const href = isFile ? fileHref(r.id) : r.url;
    return (
        <a href={href || undefined} target="_blank" rel="noopener noreferrer"
            className={`rounded-xl border p-4 flex flex-col justify-center gap-2 transition-all hover:shadow-lg ${flatCard} ${href ? '' : 'opacity-60 pointer-events-none'}`}>
            <span className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-indigo-900/40 text-indigo-300' : 'bg-indigo-50 text-indigo-600'}`}>
                <FileText size={18} />
            </span>
            <p className={`text-sm font-bold line-clamp-2 ${isDarkMode ? 'text-indigo-300' : 'text-indigo-700'}`}>{r.title}</p>
            <span className={`inline-flex items-center gap-1 text-xs font-semibold ${isDarkMode ? 'text-indigo-400' : 'text-indigo-600'}`}>
                {isFile ? <><Download size={13} /> Download</> : <><ExternalLink size={13} /> Open</>}
            </span>
        </a>
    );
};

export default function TutorialsSupport() {
    const { isDarkMode } = useTheme();
    const [resources, setResources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [activeVideoId, setActiveVideoId] = useState(null);
    const [loaded, setLoaded] = useState({});
    const markLoaded = (id) => setLoaded((p) => (p[id] ? p : { ...p, [id]: true }));

    useEffect(() => {
        let alive = true;
        (async () => {
            try {
                const res = await api.get('/resources', { params: { placement: 'tutorials' } });
                if (alive) setResources(Array.isArray(res.data?.items) ? res.data.items : []);
            } catch {
                if (alive) setResources([]);
            } finally {
                if (alive) setLoading(false);
            }
        })();
        return () => { alive = false; };
    }, []);

    const pageBg = `min-h-screen ${isDarkMode ? 'bg-surface' : 'bg-surface'}`;
    const heading = isDarkMode ? 'text-slate-100' : 'text-slate-900';
    const sub = isDarkMode ? 'text-slate-400' : 'text-slate-600';
    const skBase = isDarkMode ? '#334155' : '#e2e8f0';
    const skHi = isDarkMode ? '#475569' : '#f1f5f9';
    const flatCard = isDarkMode ? 'bg-slate-800/50 border-slate-700 hover:border-indigo-500' : 'bg-white border-indigo-100 hover:border-indigo-300';

    return (
        <div className={pageBg}>
            <div className="max-w-4xl mx-auto px-4 py-8">
                <h1 className={`text-3xl font-extrabold ${heading}`}>Tutorials</h1>
                <p className={`mt-2 ${sub}`}>Guides and walkthroughs to help you get the most out of the app.</p>

                <section className={`mt-6 rounded-2xl border shadow-sm overflow-hidden ${isDarkMode ? 'bg-slate-800/50 border-slate-700' : 'bg-indigo-50/30 border-indigo-100'}`}>
                    <div className={`px-4 py-3 md:px-6 md:py-4 border-b flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 ${isDarkMode ? 'border-slate-700 bg-slate-800' : 'border-indigo-100 bg-indigo-50/50'}`}>
                        <h2 className={`text-lg md:text-xl font-bold flex items-center gap-2 ${heading}`}>
                            <Video className={isDarkMode ? 'text-indigo-400' : 'text-indigo-600'} size={22} />
                            Learning Resources
                        </h2>
                        <a href="https://www.youtube.com/@BuildMyBizBrand" target="_blank" rel="noopener noreferrer"
                            className={`inline-flex items-center gap-1.5 text-sm font-semibold shrink-0 ${isDarkMode ? 'text-red-400 hover:text-red-300' : 'text-red-600 hover:text-red-700'}`}>
                            <Youtube size={18} />
                            Follow our YouTube channel @BuildMyBizBrand
                        </a>
                    </div>

                    <div className="p-4 md:p-6">
                        {loading ? (
                            <SkeletonTheme baseColor={skBase} highlightColor={skHi}>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                                    {[0, 1, 2].map((i) => (
                                        <div key={i} className="rounded-xl overflow-hidden"><Skeleton className="!block aspect-video !h-auto" style={{ aspectRatio: '16/9' }} /><div className="p-3"><Skeleton width="70%" /></div></div>
                                    ))}
                                </div>
                            </SkeletonTheme>
                        ) : resources.length === 0 ? (
                            <p className={`py-8 text-center text-sm ${sub}`}>No tutorials yet — check back soon.</p>
                        ) : (
                            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 items-stretch">
                                {resources.map((r) => (r.source === 'youtube'
                                    ? <VideoCard key={r.id} r={r} isDarkMode={isDarkMode} sub={sub} flatCard={flatCard}
                                        loaded={loaded} markLoaded={markLoaded} onOpen={setActiveVideoId} />
                                    : <LinkCard key={r.id} r={r} isDarkMode={isDarkMode} flatCard={flatCard} />))}
                            </div>
                        )}
                    </div>
                </section>
            </div>

            <VideoModal isOpen={!!activeVideoId} videoId={activeVideoId} onClose={() => setActiveVideoId(null)} isDarkMode={isDarkMode} />
        </div>
    );
}
